import {
  CreativeManagementAction,
  DefaultCreativeManagementStateFactory
} from 'components/CampaignCreativeManagement/CreativeManagementStateFactory';
import { RtbCampaigns } from 'containers/RtbCampaigns';
import { RtbCampaignDrafts } from 'containers/RtbCampaigns/RtbCampaignDrafts';
import { DefaultRtbCampaignDraftsModel } from 'containers/RtbCampaigns/RtbCampaignDraftsModel';
import { CampaignList } from 'containers/RtbCampaigns/RtbCampaignList/CampaignList';
import { DefaultCampaignListModel } from 'containers/RtbCampaigns/RtbCampaignList/CampaignListModel';
import { DefaultRtbCampaignsModel } from 'containers/RtbCampaigns/RtbCampaignsModel';
import { AddonFeatureManager, LocaleMeta } from 'core';
import { L1Object } from 'core/l1Object/L1Object';
import {
  DefaultL1ObjectManager,
  L1ObjectManager
} from 'core/l1Object/L1ObjectManager';
import { BidStrategy } from 'core/l2Object/L2Object';
import { Order } from 'core/order/Order';
import { createCreativeBindDataByCampaign } from 'core/rtbCampaign/CreativeBindData';
import { RtbCampaignListBasic } from 'core/rtbCampaign/RtbCampaign';
import { buildValueLabelMap } from 'utils/SelectOptionsUtils';
import {
  DELIVERY_TARGET_LIMITATION_UNITS,
  DeliveryTargetParentOptions
} from 'components/LimitationTableCell/groupStoreLimitations';
import { overlayLimitationData } from 'utils/LimitationUtil';
import {
  DefaultRtbCampaignManager,
  RtbCampaignManager
} from 'core/rtbCampaign/RtbCampaignManager';
import { formatPriceWithCurrency } from 'helper/CurrencyHelper';
import i18n from 'i18n';
import _ from 'lodash';
import { History } from 'history';
import { DefaultTrackingManagementStateFactory } from './TrackingManagement/TrackingManagementStateFactory';
import {
  DefaultLimitationManagementStateFactory,
  LimitationManagementAction
} from './LimitationManagement/LimitationManagementStateFactory';
import { DefaultScheduleManagementStateFactory } from './ScheduleManagement/ScheduleManagementStateFactory';
import { getL1ObjectChannelName } from 'core/channelGroup/channelDisplayName';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import { ChannelType } from 'core/channelGroup/ChannelGroup';
import {
  AdRequestSourceManager,
  DefaultAdRequestSourceManager
} from 'core/adRequestSource/AdRequestSourceManager';

export type L1ObjectComponentsData = {
  draftsComponent: {
    component: any;
    props: any;
  };
  campaignsComponent: {
    component: any;
    props: any;
  };
  listComponent: {
    component: any;
    props: any;
  };
};

export interface L1ObjectChannelDetailModel {
  readonly l2ObjectList: any[];
  readonly componentsData?: L1ObjectComponentsData;
  initChannelModel: (defaultSearchString?: string | null) => Promise<void>;
  getCreativeManagementStateFactory: (
    campaignIds: string[],
    draftIds: string[],
    actionParam: string,
    history: History
  ) => DefaultCreativeManagementStateFactory | undefined;
  getTrackingManagementStateFactory?: (
    campaignIds: string[]
  ) => DefaultTrackingManagementStateFactory | undefined;
  getLimitationManagementStateFactory?: (
    campaignIds: string[],
    actionParam: string
  ) => DefaultLimitationManagementStateFactory | undefined;
  getScheduleManagementStateFactory?: (
    campaignIds: string[]
  ) => DefaultScheduleManagementStateFactory | undefined;
  getL1ObjectBasicViewData(): any;
  getL1ObjectChannelViewData(): any;
}

class DefaultCampaignGroupDetailModel implements L1ObjectChannelDetailModel {
  l2ObjectList: RtbCampaignListBasic[] = [];
  componentsData?: L1ObjectComponentsData;
  /**
   * 戶外通路才有，由子類在取活動時一併填好，供列表把門市／機台依縣市分組，
   * 並把「整個縣市」還原成它底下的每一間。
   */
  // 測試活動與正式活動看的是兩棵不同的選項樹，所以依 isTestMode 各存一份。
  protected deliveryTargetParentOptions?: Map<
    boolean,
    DeliveryTargetParentOptions
  >;

  constructor(
    protected order: Order,
    protected l1Object: L1Object,
    protected addonFeatureManager: AddonFeatureManager,
    protected refreshGroupDetail: (onFinish?: () => void) => Promise<void>,
    protected localeMeta?: LocaleMeta,
    protected campaignManager: RtbCampaignManager = new DefaultRtbCampaignManager(),
    protected l1ObjectManager: L1ObjectManager = new DefaultL1ObjectManager()
  ) {}

  /**
   * Hook for subclasses to post-process the campaign fetch — e.g.
   * SupportOutdoorTACampaignGroupDetailModel overlays freshly-resolved labels
   * onto outdoor limitation values before list models are built.
   */
  protected async getCampaignsOfGroup(): Promise<RtbCampaignListBasic[]> {
    return this.campaignManager.getCampaignsOfGroup(this.l1Object.l1ObjectId);
  }

  async initChannelModel(defaultSearchString?: string | null) {
    try {
      this.l2ObjectList = await this.getCampaignsOfGroup();
      const campaignListModel = new DefaultCampaignListModel(
        this.order,
        this.l1Object,
        this.l2ObjectList,
        this.addonFeatureManager,
        this.refreshGroupDetail,
        (searchString: string) => {
          const url = new URL(window.location.href);
          if (searchString) {
            url.searchParams.set('search', searchString);
          } else {
            url.searchParams.delete('search');
          }
          window.history.replaceState(null, '', url.toString());
        },
        defaultSearchString ? defaultSearchString : '',
        this.l1Object.budgetBalance,
        this.localeMeta,
        this.deliveryTargetParentOptions
      );
      const campaignsModel = new DefaultRtbCampaignsModel(
        this.order,
        this.addonFeatureManager,
        this.l1Object,
        this.l2ObjectList
      );
      const campaignDraftsModel = new DefaultRtbCampaignDraftsModel(
        this.order,
        this.addonFeatureManager,
        this.l1Object,
        this.l2ObjectList
      );
      this.componentsData = {
        draftsComponent: {
          component: RtbCampaignDrafts,
          props: {
            model: campaignDraftsModel
          }
        },
        campaignsComponent: {
          component: RtbCampaigns,
          props: {
            model: campaignsModel
          }
        },
        listComponent: {
          component: CampaignList,
          props: {
            model: campaignListModel
          }
        }
      };
    } catch (e) {
      console.error(e);
    }
  }

  getCreativeManagementStateFactory(
    campaignIds: string[],
    draftIds: string[],
    actionParam: string,
    history: History
  ) {
    const creativeBindData = this.l2ObjectList.map((l2Object) =>
      createCreativeBindDataByCampaign(
        l2Object,
        this.l1Object.channel,
        this.l1Object.channelGroupId
      )
    );
    const campaigns = getCampaignBindList(
      creativeBindData,
      campaignIds,
      draftIds
    );
    const allActions: string[] = Object.values(CreativeManagementAction);
    if (campaigns.length === 0 || !allActions.includes(actionParam)) {
      return;
    }

    return new DefaultCreativeManagementStateFactory(
      campaigns,
      this.order.advertiserId,
      actionParam,
      this.addonFeatureManager,
      this.order.orderNumber,
      history,
      this.l1Object
    );
  }

  getTrackingManagementStateFactory(
    campaignIds: string[]
  ): DefaultTrackingManagementStateFactory | undefined {
    const campaigns = this.l2ObjectList.filter(
      (l2Object) =>
        l2Object.id &&
        campaignIds.includes(l2Object.id.toString()) &&
        !l2Object.isDraft
    );
    return new DefaultTrackingManagementStateFactory(
      this.order,
      this.l1Object,
      campaigns.map((campaign) => ({
        id: campaign.id!,
        name: campaign.name,
        adType: campaign.adType
      })),
      this.addonFeatureManager
    );
  }

  getLimitationManagementStateFactory(
    campaignIds: string[],
    actionParam: string
  ): DefaultLimitationManagementStateFactory | undefined {
    const allActions: string[] = Object.values(LimitationManagementAction);

    const campaigns = this.l2ObjectList.filter(
      (l2Object) =>
        l2Object.id &&
        campaignIds.includes(l2Object.id.toString()) &&
        !l2Object.isDraft
    );

    if (campaigns.length === 0 || !allActions.includes(actionParam)) {
      return;
    }

    return new DefaultLimitationManagementStateFactory(
      this.order,
      this.l1Object,
      actionParam,
      campaigns.map((campaign) => ({
        id: campaign.id!,
        name: campaign.name,
        adType: campaign.adType,
        adTypeId: campaign.adTypeId,
        limitations: campaign.additionalInfo.limitations
      })),
      this.addonFeatureManager,
      this.localeMeta
    );
  }

  getScheduleManagementStateFactory(
    campaignIds: string[]
  ): DefaultScheduleManagementStateFactory | undefined {
    const campaigns = this.l2ObjectList.filter(
      (l2Object) => l2Object.id && campaignIds.includes(l2Object.id.toString())
    );
    return new DefaultScheduleManagementStateFactory(
      this.order,
      campaigns.map((campaign) => ({
        id: campaign.id!,
        name: campaign.name,
        startDate: campaign.startDate,
        endDate: campaign.endDate
      }))
    );
  }

  getL1ObjectChannelViewData() {
    const rtbBidStrategyKey = Object.keys(BidStrategy).find(
      (key) => BidStrategy[key] === _.get(this.l1Object, 'rtb.bid_strategy', '')
    );
    const viewableImpressReport = _.get(
      this.l1Object,
      'rtb.viewable_impress_report',
      false
    );
    const showViewableImpressReportSetting =
      !this.l1ObjectManager.isOutdoorChannel(this.l1Object.channel);
    return _.compact([
      {
        label: i18n.t('l1Object.labels.objectiveLabel'),
        value: this.l1Object.objective
          ? i18n.t(
              `l1Object.labels.objective.${this.l1Object.objective.toLowerCase()}`
            )
          : ''
      },
      this.l1Object.autoOptimise
        ? {
            label: i18n.t('l1Object.labels.bidStrategyLabel'),
            value: rtbBidStrategyKey
              ? i18n.t(
                  `l1Object.labels.bidStrategy.${rtbBidStrategyKey.toLowerCase()}`
                )
              : ''
          }
        : undefined,
      showViewableImpressReportSetting
        ? {
            label: i18n.t('l1Object.labels.viewableImpressReport'),
            value: viewableImpressReport
              ? i18n.t('common.labels.yes')
              : i18n.t('common.labels.no')
          }
        : undefined
    ]);
  }

  getL1ObjectBasicViewData() {
    const getBudgetValue = (l1Object: L1Object, currency: string) => {
      const hasBudget = !_.isNil(l1Object.budget);
      const hasDailyBudget = !_.isNil(l1Object.dailyBudget);
      if (hasBudget) {
        return formatPriceWithCurrency(currency, +l1Object.budget!);
      }
      if (hasDailyBudget) {
        return `${formatPriceWithCurrency(currency, +l1Object.dailyBudget!)} / ${i18n.t('campaign.labels.day')}`;
      }
      return i18n.t('common.labels.noData');
    };

    let budgetBalanceSummary: any = {
      label: i18n.t('l1Object.labels.budgetBalance'),
      value: formatPriceWithCurrency(
        this.order.currency,
        this.l1Object.budgetBalance
      )
    };
    let cboInfo = i18n.t('common.labels.no');
    if (this.l1Object.autoOptimise) {
      budgetBalanceSummary = undefined;
      cboInfo = i18n.t('common.labels.yes');
    }
    return _.compact([
      {
        label: i18n.t('l1Object.labels.name'),
        value: this.l1Object.name
      },
      {
        label: i18n.t('l1Object.labels.channel'),
        value: getL1ObjectChannelName(
          this.l1Object.channel,
          this.l1Object.channelGroupId
        )
      },
      {
        label: i18n.t('l1Object.labels.budget'),
        value: getBudgetValue(this.l1Object, this.order.currency)
      },
      budgetBalanceSummary,
      {
        label: i18n.t('l1Object.labels.autoOptimise'),
        value: cboInfo
      }
    ]);
  }
}

class SupportOutdoorTACampaignGroupDetailModel extends DefaultCampaignGroupDetailModel {
  constructor(
    order: Order,
    l1Object: L1Object,
    addonFeatureManager: AddonFeatureManager,
    refreshGroupDetail,
    localeMeta?: LocaleMeta,
    private adRequestSourceManager: AdRequestSourceManager = new DefaultAdRequestSourceManager()
  ) {
    super(order, l1Object, addonFeatureManager, refreshGroupDetail, localeMeta);
  }

  /**
   * Fetch campaigns and overlay outdoor space-attribute labels onto each
   * limitation's `value` (under `additionalInfo.limitations`). Labels come
   * from the freshly-fetched getOutdoorSpaceAttributes bag (server-resolved
   * per current locale), so campaign-list rendering shows current-locale
   * labels even for campaigns saved in another locale.
   */
  protected override async getCampaignsOfGroup(): Promise<
    RtbCampaignListBasic[]
  > {
    const campaigns = await super.getCampaignsOfGroup();
    const channelGroupSetting = ChannelGroupSetting.fromKey(
      this.l1Object.channel
    );
    const channelGroupId = channelGroupSetting.config.id;
    if (!channelGroupId) return campaigns;
    // MOVABLE_OUTDOOR has no store/device space attributes — skip the bag
    // fetch + overlay since there's nothing to localize on customGeofence /
    // administrativeArea limitation rows (RKB-2541 §9.3a).
    if (
      channelGroupSetting.config.channelType === ChannelType.MOVABLE_OUTDOOR
    ) {
      return campaigns;
    }
    // 測試模式與正式模式是兩棵不同的選項樹：測試活動鎖的是測試版位，拿正式那棵
    // 去對，label 查不到、整縣市也展不出正確的門市。只抓這批活動真的用到的模式，
    // 通常只有一種。
    const modes = Array.from(new Set(campaigns.map((c) => !!c.isTestMode)));
    const bags = await Promise.all(
      modes.map(async (mode) => ({
        mode,
        bag: await this.adRequestSourceManager.getOutdoorSpaceAttributes(
          channelGroupId,
          mode
        )
      }))
    );

    const labelMapsByMode = new Map<
      boolean,
      Map<string, Map<string | number, string>>
    >();
    const parentOptionsByMode = new Map<boolean, DeliveryTargetParentOptions>();
    this.deliveryTargetParentOptions = parentOptionsByMode;
    bags.forEach(({ mode, bag }) => {
      const labelMaps = new Map<string, Map<string | number, string>>();
      Object.keys(bag).forEach((type) => {
        labelMaps.set(type, buildValueLabelMap(bag[type]));
      });
      labelMapsByMode.set(mode, labelMaps);
      // 門市／機台的選項是「縣市 → 門市（機台）」兩層，縣市層節點就是「整個縣市」。
      // 選整個縣市時活動只存得下縣市那一個 value，列表要把底下每一間列出來就得
      // 靠這棵樹回查。同一份 bag 上面已經拿回來了，不必再打一次。
      parentOptionsByMode.set(
        mode,
        Object.keys(DELIVERY_TARGET_LIMITATION_UNITS).reduce((acc, type) => {
          acc[type] = new Map(
            (bag[type] ?? []).map((option) => [
              option.value.toString(),
              option.options ?? []
            ])
          );
          return acc;
        }, {} as DeliveryTargetParentOptions)
      );
    });

    for (const campaign of campaigns) {
      const limitations = campaign.additionalInfo?.limitations;
      const labelMaps = labelMapsByMode.get(!!campaign.isTestMode);
      if (limitations && labelMaps) {
        overlayLimitationData(limitations, labelMaps);
      }
    }
    return campaigns;
  }
}

export function getL1ObjectChannelDetailModel(
  order: Order,
  l1Object: L1Object,
  addonFeatureManager: AddonFeatureManager,
  refreshGroupDetail: (onFinish?: () => void) => Promise<void>,
  localeMeta?: LocaleMeta,
  l1ObjectManager: L1ObjectManager = new DefaultL1ObjectManager()
): L1ObjectChannelDetailModel | undefined {
  const needLocaleMetaArgs: [
    Order,
    L1Object,
    AddonFeatureManager,
    (onFinish?: () => void) => Promise<void>,
    LocaleMeta | undefined
  ] = [order, l1Object, addonFeatureManager, refreshGroupDetail, localeMeta];

  if (l1ObjectManager.isChannelSupportOutdoorTA(l1Object.channel)) {
    return new SupportOutdoorTACampaignGroupDetailModel(...needLocaleMetaArgs);
  }

  return new DefaultCampaignGroupDetailModel(...needLocaleMetaArgs);
}

function getCampaignBindList(creativeBindData, campaignIds, draftIds) {
  return creativeBindData
    ? creativeBindData.filter((campaign) =>
        campaign.draftId
          ? draftIds.includes(campaign.draftId.toString())
          : campaign.id && campaignIds.includes(campaign.id.toString())
      )
    : [];
}
